import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Calendar, Settings, Home, AlertTriangle } from "lucide-react";

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

const LEGAL_DISCLAIMER_USA = {
  title: "Important Legal Notice",
  text: "Apoyum Listeners are NOT licensed therapists, psychologists, or mental health professionals in the United States. This service is for emotional support and companionship only, NOT professional therapy. Listeners may be located outside the U.S. If you are in crisis, please contact a mental health professional or call 988 (Suicide & Crisis Lifeline)."
};

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "listener") return "/listener";
    return "/mis-sesiones";
  };

  return (
    <header className="sticky-header">
      <div className="container-main">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Apoyum" className="h-10 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/explorar" className="text-foreground/80 hover:text-primary font-medium transition-colors">
              Explorar
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="dashboard-link">
                    <Home className="w-4 h-4 mr-2" />
                    Mi panel
                  </DropdownMenuItem>
                  {user.role === "usuario" && (
                    <DropdownMenuItem onClick={() => navigate("/mis-sesiones")} data-testid="my-sessions-link">
                      <Calendar className="w-4 h-4 mr-2" />
                      Mis sesiones
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">Iniciar sesión</Button>
                </Link>
                <Link to="/registro">
                  <Button className="btn-primary" data-testid="register-btn">Registrarse</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-stone-100 border-t border-stone-200">
      <div className="container-main py-12">
        {/* Legal Disclaimer Box - USA Compliance */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 text-sm mb-1">{LEGAL_DISCLAIMER_USA.title}</h4>
              <p className="text-xs text-amber-800 leading-relaxed">{LEGAL_DISCLAIMER_USA.text}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <Link to="/" className="inline-block">
              <img src="/logo.png" alt="Apoyum" className="h-12 w-auto" />
            </Link>
            <p className="mt-4 text-stone-600 max-w-md">
              Conectamos personas que buscan acompañamiento emocional con listeners capacitados para escuchar y guiar.
            </p>
            <p className="mt-6 legal-disclaimer" data-testid="footer-disclaimer">
              {LEGAL_DISCLAIMER}
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-foreground mb-4">Enlaces</h4>
            <ul className="space-y-2">
              <li><Link to="/explorar" className="text-stone-600 hover:text-primary transition-colors">Explorar</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-foreground mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terminos" className="text-stone-600 hover:text-primary transition-colors">Términos de uso</Link></li>
              <li><Link to="/privacidad" className="text-stone-600 hover:text-primary transition-colors">Privacidad</Link></li>
              <li><Link to="/participar" className="text-stone-500 hover:text-stone-600 transition-colors text-sm">¿Quieres ser listener?</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-stone-200 text-center text-stone-500">
          <p>© {new Date().getFullYear()} Apoyum. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col bg-background noise-bg">
      <Header />
      <main className="flex-1 pt-16">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

export default Layout;
